"use client";

import { useSearchParams } from "next/navigation";
import { type ReactNode, Suspense, useEffect } from "react";
import { type BillingCycle, type PlanTier, useOnboardingState } from "@/hooks/useOnboardingState";
import { OnboardingModal } from "./OnboardingModal";

type OnboardingProviderProps = {
  children: ReactNode;
};

/**
 * OnboardingProvider wraps the app and handles:
 * - Reading URL parameters (?plan=pro&billing=annual) on mount
 * - Setting initial onboarding state from URL params
 * - Rendering the OnboardingModal
 *
 * DETERMINISTIC SHELL PATTERN:
 * - OnboardingModal is ALWAYS rendered in the DOM (no isClient conditional)
 * - Modal controls its own visibility internally via CSS/portal
 * - Prevents hydration mismatch and shell replacement effects
 * - Ensures cold load, reload, and return-to-top shell stability
 *
 * SUSPENSE-SAFE PATTERN:
 * - useSearchParams() is isolated in OnboardingUrlParamSync, wrapped in <Suspense>
 * - This prevents the layout from being forced into a Suspense boundary
 * - The modal renders unconditionally; only param-sync is deferred
 *
 * @example
 * ```tsx
 * // In app layout or page
 * <OnboardingProvider>
 *   <App />
 * </OnboardingProvider>
 * ```
 *
 * Supported URL Parameters:
 * - `plan`: "free" | "starter" | "pro" | "studio"
 * - `billing`: "monthly" | "annual"
 *
 * Example URLs:
 * - `/?plan=pro&billing=annual` - Pre-select Pro plan with annual billing
 * - `/?plan=starter` - Pre-select Starter plan with default monthly billing
 */
export function OnboardingProvider({ children }: OnboardingProviderProps) {
  return (
    <>
      {children}
      <Suspense fallback={null}>
        <OnboardingUrlParamSync />
      </Suspense>
      {/*
        OnboardingModal is always rendered for deterministic shell.
        Modal controls visibility internally to prevent shell replacement.
      */}
      <OnboardingModal />
    </>
  );
}

/**
 * Internal component that reads URL search params and syncs them to onboarding state.
 * Isolated here so that useSearchParams() does not force the parent layout into a
 * Suspense boundary — Next.js requires components calling useSearchParams() to be
 * wrapped in <Suspense>.
 */
function OnboardingUrlParamSync() {
  const { actions, state } = useOnboardingState();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Only process URL params if window is available (client-side only)
    if (typeof window === "undefined") {
      return;
    }

    // searchParams can be null in test environments or during SSR
    if (!searchParams) {
      return;
    }

    const planParam = searchParams.get("plan");
    const billingParam = searchParams.get("billing");
    const refParam = searchParams.get("ref");

    // Capture referral code from URL if present and not already applied
    if (refParam && refParam.length === 8 && !state.referralApplied) {
      actions.setReferralCode(refParam.toUpperCase());
    }

    // Validate and set plan if present in URL
    if (planParam) {
      const validPlans: PlanTier[] = ["free", "starter", "pro", "studio"];
      if (validPlans.includes(planParam as PlanTier)) {
        const plan = planParam as PlanTier;
        const billing =
          billingParam === "annual" || billingParam === "monthly"
            ? (billingParam as BillingCycle)
            : state.billingCycle;

        actions.setPlan(plan, billing);

        // If a plan is specified in URL and onboarding isn't complete, open modal
        if (!state.onboardingComplete) {
          actions.openModal();
        }
      }
    }

    // Set billing cycle if present (even without plan param)
    if (billingParam && !planParam && (billingParam === "annual" || billingParam === "monthly")) {
      actions.setBillingCycle(billingParam as BillingCycle);
    }
  }, [searchParams, actions, state.billingCycle, state.onboardingComplete, state.referralApplied]);

  return null;
}
